import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingCycle, PlanFeatureItem } from 'webapp/enums/enums';
import { capitalize } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import AppNotificationsPausedOrDiscountedComponent from 'webapp/components/app-notifications/paused-or-discounted';
import BillingAddonsComponent from 'webapp/components/billing/addons';
import BillingPlanComponent from 'webapp/components/billing/plan';
import BillingPlanFeaturesTableComponent, {
	SECTIONS as PlanFeaturesTableSections,
} from 'webapp/components/billing/plan-features-table';
import BillingUnverifiedAgencyWarningComponent from 'webapp/components/billing/unverified-agency-warning';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon.gts';

import type { TrackingOriginForSubscriptionChangePopup } from 'webapp/services/billing';

type Args = Record<string, never>;

export default class BillingPlansComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('flow/annual-plan') declare flowAnnualPlanService: Service.Flow.AnnualPlan;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;

	@tracked selectedInterval;

	trackingOrigin = 'pricing_page' as TrackingOriginForSubscriptionChangePopup;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const defaultBillingCycle = this.flowAnnualPlanService.isEligibleForRegularFlow
			? BillingCycle.YEARLY
			: BillingCycle.MONTHLY;
		this.selectedInterval = this.billingService.finalPlan?.billingCycle || defaultBillingCycle;
	}

	intervalValues = [
		{ value: BillingCycle.MONTHLY, label: 'Monthly' },
		{ value: BillingCycle.QUARTERLY, label: 'Quarterly' },
		{ value: BillingCycle.YEARLY, label: 'Yearly' },
	];

	socialProofFirstImageSet = [
		{
			path: 'https://cdn1.databox.com/analytics/billing/g2_crowd.png',
			height: 56,
			alt: 'G2 Crowd',
		},
		{
			path: 'https://cdn1.databox.com/analytics/billing/crowd_awards.png',
			height: 128,
			alt: 'Crowd Awards',
		},
		{
			path: 'https://cdn1.databox.com/analytics/billing/capterra.png',
			height: 56,
			alt: 'Capterra',
		},
	];

	socialProofSecondImageSet = [
		{
			path: 'https://cdn1.databox.com/analytics/billing/epicosity.png',
			alt: 'Epicosity',
		},
		{
			path: 'https://cdn1.databox.com/analytics/billing/smartbug.png',
			alt: 'Smartbug',
		},
		{
			path: 'https://cdn1.databox.com/analytics/billing/jellyfish.png',
			alt: 'Jellyfish',
		},
		{
			path: 'https://cdn1.databox.com/analytics/billing/jackmorton.png',
			alt: 'Jack Morton',
		},
		{
			path: 'https://cdn1.databox.com/analytics/billing/sagefrog.png',
			alt: 'Sagefrog',
		},
		{
			path: 'https://cdn1.databox.com/analytics/billing/weidertgroup.png',
			alt: 'Weidert Group',
		},
	];

	frequentlyAskedQuestions = [
		{
			question: 'How do you protect privacy and ensure security?',
			answer: 'We take data security very seriously. Amazon is our hosting partner, and our servers are hosted in a world-class data center with the most advanced <a href="https://databox.com/security-policy" target="_blank">security</a> protocols. Read more about how we protect and ensure the <a href="https://databox.com/privacy-policy" target="_blank">privacy of your data</a>.',
		},
		{
			question: 'Can I upgrade and downgrade at any time?',
			answer: 'Absolutely. Our flexible payment plans allow you to upgrade, downgrade, or cancel at any time. When upgrading, you’re only responsible for the difference in cost between your current plan and your new plan. When downgrading, your plan changes immediately and you’ll receive a credit applied to future payments covering the difference in cost between the current plan and the new plan. Cancellations take effect at the end of your billing period.',
		},
		{
			question: 'Do you offer a yearly discount?',
			answer: 'Yes. Upgrade to a yearly subscription and get 20% off.',
		},
		{
			question: 'Which payment methods do you accept?',
			answer: 'You can pay for your subscription using Visa, Mastercard, Maestro, American Express, JCB, Discover, Diners Club, Union Pay, and Google Pay. Annual subscriptions can be billed with other payment methods (wire bank transfer, checks,...). Contact us for more information.',
		},
		{
			question: 'What happens after my free trial?',
			answer: 'During the 14-day trial, you can purchase a paid plan and your first payment will be at the end of the trial period. If you do not purchase, the trial will expire and you’ll be prompted to enter the credit card to continue using Databox. We do not require a credit card to start a trial.',
		},
		{
			question: 'What if I exceed my plan’s limits?',
			answer: 'No problem, we will automatically notify you within the app when you run into a limit for your plan. Common limits you get notified of are the number of users, dashboards, data sources, built metrics, and when trying to use certain advanced features not included in your plan.',
		},
		{
			question: 'How can I cancel my subscription?',
			answer: 'You can cancel your subscription at any time from the bottom section on the plans or billing page. Our support team can help if you just need to pause your account temporarily.',
		},
		{
			question: 'What support resources are available to me?',
			answer: 'World-class support is included in all plans! Everyone gets access to:<ul><li>Our support team - just start a chat with us</li><li><a href="https://help.databox.com" target="_blank">Help center</a></li></ul>',
		},
		{
			question: 'What currency is used for billing?',
			answer: 'All prices listed are shown in US dollars (USD).',
		},
		{
			question: 'What is the difference between a data source and a connection?',
			answer: "<p>A <strong>connection</strong> refers to the integration you set up to bring in data from a specific service or tool, such as Google Analytics.</p><br><p>A <strong>data source</strong> is a specific dataset or view within that connection. For instance, in Google Analytics, a data source is a particular website property, such as a specific website's domain.</p><br><p>One connection can encompass multiple data sources. So, while you have a single connection to Google Analytics, you can access and use data from multiple properties (domains) within that connection.</p>",
		},
	];

	@cached
	get plansForCurrentInterval() {
		const availablePlans = this.billingService.availablePlans;

		let plans = availablePlans.filter((plan) => plan.billingCycle === this.selectedInterval);
		const freePlan = availablePlans.find((plan) => plan.isFree && !plan.isLegacy);
		const customPlan = availablePlans.find((plan) => plan.isCustom);
		if (freePlan) {
			plans = [freePlan, ...plans];
		}
		if (customPlan) {
			plans = [...plans, customPlan];
		}

		return plans;
	}

	// Pairs each visible plan with the one rendered before it, so the card can show
	// "Everything in <previous plan>, plus:".
	get plansWithPrevious() {
		const plans = this.plansForCurrentInterval;
		return plans.map((plan, index) => {
			let previousPlan = plans[index - 1];
			if (plan.isTeamScale && previousPlan?.isTeamCore) {
				// for both team plans we compare with the previous card
				previousPlan = plans[index - 2];
			}
			return { plan, previousPlan };
		});
	}

	get isUltimateAgencyPlan() {
		// show features list instead of the table
		return this.billingService.agencyPricing && this.billingService.is2026SeptBillingGroup;
	}

	get additionalFeaturesInfoForUltimateAgencyPlan() {
		// showing all features in 4 columns (instead of the PlanFeaturesTable)

		const columns = PlanFeaturesTableSections.slice(0, 3).map((section) => {
			const features: { name: string; info?: string; isSoon?: boolean }[] = [];

			section.items.forEach((item) => {
				const itemInfo = this.billingService.getPlanFeatureItemInfo(item);

				let name = itemInfo.name;
				const description = itemInfo.description,
					isSoon = !!itemInfo.isSoon;

				if (itemInfo.hideOn2026SeptView) {
					return;
				}

				const value = this.billingService.activePlan!.getPlanFeatureItemValue(item);

				const showAsIsIfHasValue = [PlanFeatureItem.CUSTOM_METRICS];

				// only showing enabled features, plus a few exceptions
				if (item === PlanFeatureItem.MAX_SYNC_FREQUENCY) {
					name = `${capitalize(value.text || '')} sync`;
				} else if (item === PlanFeatureItem.DASHBOARDS_REPORTS) {
					const count = value.text === '∞' ? 'Unlimited' : value.text;
					name = `${count} dashboards & reports`;
				} else if (showAsIsIfHasValue.includes(item)) {
					// some countable features we want to show anyways, but only if they have value
					if (value.text === '0') {
						return;
					}
				} else if (!value.flag) {
					// flag-like feature thats not enabled
					return;
				}

				features.push({
					name,
					info: description,
					isSoon: !!isSoon,
				});
			});

			return {
				title: section.name,
				features,
			};
		});

		columns.push({
			title: 'Built for agencies',
			features: [
				{ name: 'Scales as you grow' },
				{ name: 'A separate account for every client' },
				{ name: 'Bulk actions' },
				{ name: 'Custom templates' },
				{ name: 'Cross-account reporting via Genie & MCP' },
				{ name: 'Become a Solutions Partner & earn commissions' },
			],
		});

		return columns;
	}

	get showCancelSubscription() {
		const { isCanceled, isTrial, activePlan } = this.billingService;
		return !activePlan?.isFree && !isTrial && !isCanceled;
	}

	get isCancelSubscriptionBlocked() {
		return this.billingService.blockDowngradeOrCancelDueCommitment;
	}

	get isAddonSectionLastRow() {
		return !this.isSocialProofSectionLastRow && !this.billingService.isPayingCustomer;
	}

	get isSocialProofSectionLastRow() {
		return !this.showCancelSubscription;
	}

	get isAnnualPromoForRegularSignupsShown() {
		return (
			this.flowAnnualPlanService.isEligibleToShowYearlyNotification &&
			(this.selectedInterval === BillingCycle.MONTHLY ||
				this.selectedInterval === BillingCycle.QUARTERLY)
		);
	}

	get visiblePlansContainCurrentPlan() {
		return this.billingService.finalPlan?.billingCycle === this.selectedInterval;
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	get showFeaturesTable() {
		if (this.billingService.agencyPricing && this.billingService.is2026SeptBillingGroup) {
			return false;
		}
		return true;
	}

	@action
	changeInterval(value: AnySimpleValue) {
		this.selectedInterval = value as BillingCycle;
	}

	@action
	toggleQuestionOpen(event: PointerEvent) {
		(event.target as HTMLElement)
			?.closest('.faq__entry')
			?.classList.toggle('faq__entry--shown');
	}

	@action
	cancelSubscription() {
		this.billingService.startCancellation(this.trackingOrigin);
	}

	<template>
		{{#if this.flowCancellationService.billingNotificationText}}
			<AppNotificationsPausedOrDiscountedComponent>
				{{this.flowCancellationService.billingNotificationText}}
			</AppNotificationsPausedOrDiscountedComponent>
		{{else if this.billingService.showUnverifiedAgencyWarning}}
			<BillingUnverifiedAgencyWarningComponent />
		{{/if}}

		<div class="row interval {{this.selectedInterval}}">
			<div class="radiobuttons">
				<FormRadiobuttonGroupComponent
					class="radiobutton-switcher"
					@name="billing-interval"
					@selected={{this.selectedInterval}}
					@options={{this.intervalValues}}
					@onChange={{this.changeInterval}}
					@theme="alternate"
				/>
			</div>
			{{#if this.isAnnualPromoForRegularSignupsShown}}
				<div class="promo">
					<div class="arrow-left"></div>
					<div class="arrow-up"></div>
					<CommonUiIconComponent @name="Gift" @size="sm" />
					<div class="promo__text">
						<span>{{{this.billingService.yearlyDiscountText}}}</span>
					</div>
				</div>
			{{/if}}
			{{#if this.canRequestSetupHelp}}
				<div class="help">
					<span>Need help?</span>
					<a
						role="button"
						href={{this.accountService.requestSetupHelpLink}}
						target="_blank"
					>
						<CommonUiIconComponent @name="Phone" @size="sm" />
						Book a call
					</a>
				</div>
			{{/if}}
		</div>

		<div
			class="row plans-interval-{{this.selectedInterval}}
				{{if this.visiblePlansContainCurrentPlan 'plans-contains-current'}}"
		>
			<div class="plans{{if this.billingService.agencyPricing ' plans--agency'}}">
				{{#each this.plansWithPrevious as |entry|}}
					<BillingPlanComponent
						@model={{entry.plan}}
						@previousModel={{entry.previousPlan}}
						@forBillingCycle={{this.selectedInterval}}
						@isCurrentPlan={{eq this.billingService.finalPlan.id entry.plan.id}}
					/>
				{{/each}}
			</div>
		</div>

		{{#if this.isUltimateAgencyPlan}}
			<div class="row agency-features">
				<div class="agency-features__header">
					<h1>Features included</h1>
				</div>
				<div class="plan__features plan__features--with-checkboxes">

					{{#each this.additionalFeaturesInfoForUltimateAgencyPlan as |addlFeatureInfo|}}
						<div>
							<div class="plan__features-list-title">
								{{addlFeatureInfo.title}}
							</div>
							<ul class="plan__features-list">
								{{#each addlFeatureInfo.features as |feature|}}
									<li class="enabled">
										<span class="value">
											<CommonUiIconComponent @name="Check" @size="md" />
										</span>
										<span>
											{{feature.name}}
											{{#if feature.isSoon}}
												(Soon)
											{{/if}}
										</span>
										{{#if feature.info}}
											<ToolTipWithIconComponent
												@text={{{feature.info}}}
												@iconName="Question"
												@maxWidth={{230}}
											/>
										{{/if}}
									</li>
								{{/each}}
							</ul>
						</div>
					{{/each}}
				</div>
			</div>
		{{/if}}

		<div class="row {{if this.isAddonSectionLastRow 'last-row'}}">
			<BillingAddonsComponent
				@forBillingCycle={{this.selectedInterval}}
				@trackingOrigin={{this.trackingOrigin}}
				@layout="grid"
			/>
		</div>

		{{#if this.showFeaturesTable}}
			<BillingPlanFeaturesTableComponent @plans={{this.plansForCurrentInterval}} />
		{{/if}}

		{{#unless this.billingService.isPayingCustomer}}
			<div class="row {{if this.isSocialProofSectionLastRow 'last-row'}}">
				<div class="social-proof">
					<div class="social-proof__title">
						An easy-to-use analytics platform for
						<span>growing businesses</span>
					</div>
					<div class="social-proof__images">
						{{#each this.socialProofFirstImageSet as |image|}}
							<img src={{image.path}} height={{image.height}} alt={{image.alt}} />
						{{/each}}
					</div>
					<div class="social-proof__text">Trusted by 20,000+ growing businesses</div>
					<div class="social-proof__images">
						{{#each this.socialProofSecondImageSet as |image|}}
							<img src={{image.path}} height="24" alt={{image.alt}} />
						{{/each}}
					</div>
				</div>
				<div class="faq">
					<div class="faq__title">
						Frequently asked questions
					</div>
					<div class="faq__list">
						{{#each this.frequentlyAskedQuestions as |entry|}}
							<div class="faq__entry">
								<div
									role="button"
									class="faq__see-all"
									{{on "click" this.toggleQuestionOpen}}
								>
									<span>{{entry.question}}</span>
									<CommonUiIconComponent @name="ChevronDown" @size="sm" />
								</div>
								<div class="faq__content">{{{entry.answer}}}</div>
							</div>
						{{/each}}
					</div>
				</div>
			</div>
		{{/unless}}

		{{#if this.showCancelSubscription}}
			<div class="row controls2 last-row">
				<div class="controls2__left">
					{{#if this.isCancelSubscriptionBlocked}}
						<p>
							Cancellation isn't available during your commitment period.
							<a
								class="link link--secondary"
								href={{this.billingService.annualCommitmentCallUrl}}
								target="_blank"
							>
								Talk to our team
							</a>
						</p>
					{{else}}
						<a role="button" class="cancel" {{on "click" this.cancelSubscription}}>
							Cancel subscription
						</a>
					{{/if}}
				</div>
			</div>
		{{/if}}
	</template>
}
